import type { Filter } from "@/types/Filter";
import type { Field } from "@/types/Field";
import {
  coerceEmptyEqualsOperator,
  flattenArrayFilterValues,
  isNoValueOperator,
} from "@/lib/utils/parser";
import {
  canonicalFieldKey,
  findFieldByKey,
} from "@/components/ConditionBuilder/condition-utils";
import {
  isAllowlistExempt,
  isAllowlistableKey,
} from "@/lib/authz/fieldAllowlist";

/**
 * Wire shape for `rowFilters` — the same normalisation the guest list applies
 * before it sends a filter set (see ViewFilterHandling), which this editor
 * never did:
 *
 *  1. **Flatten arrays.** The value cell is a multi-pick, but the backend's
 *     `Filter.value` is a single String — an array operand cannot survive the
 *     wire at all. One row per picked value (the authz merger ORs same-key rows
 *     inside its own group, so the meaning is preserved).
 *  2. **Coerce the "—" pick.** An empty operand on a discrete field means
 *     "match unset rows": `= ""` → `∅`, `≠ ""` → `⊙`.
 *  3. **Drop rows a value-taking operator left empty.** A row filter is a data
 *     ACCESS SCOPE, and the backend builds `≠ ""` as `$ne:""`, which matches
 *     every guest that has no such value — i.e. an empty `≠` row silently
 *     widens the scope instead of narrowing it. Never persist one.
 *  4. **Canonicalize the key.** A custom field's row-filter key must be
 *     `additionalFields.<name>`; the editor used to store the BARE name, which
 *     the guest-list lanes reject as `unknownFilterKey` (400) while the
 *     single-row lanes stop restricting altogether. Resolution accepts BOTH
 *     shapes, so a template loaded with a legacy bare key serializes to the
 *     canonical one — this is also why the dataType lookup goes through
 *     `findFieldByKey` rather than a bare-name map: after the picker switched to
 *     wire keys a bare-name lookup would miss every custom field, and the
 *     "—" coercion (`= ""` → `∅`) would silently stop firing.
 *
 * <p>`stored` is the template's PERSISTED rows, and passing it changes rule 3 for
 * exactly one class: a row that is already stored AND still on screen is kept
 * verbatim instead of dropped. Rule 3 is right for a row the admin is currently
 * typing — but applied to a row that predates the rule it is a silent SCOPE
 * WIDENING: templates really do carry `{firstName, "=", ""}` (the pre-gate editor
 * sent `rows.filter(f => f.key && f.operator)`, and there is a whole e2e spec for
 * it), and dropping it on an unrelated save — a rename, a capability tick — hands
 * the role a wider data scope than it had, with a success toast. Worse, once the
 * scope-wipe guard refuses that save, such a template becomes permanently
 * unsavable, which is the same defect class as PB-TU-1. The backend accepts the
 * row: `checkFilterValue` returns null for an empty value before it looks at
 * anything else. Deleting the row still removes it — it has to still be present
 * in `rows` to be preserved — so the admin keeps a way out, and it is the guard,
 * not the serializer, that then explains what deleting the last row means.
 */
export function serializeRowFilters(
  rows: Filter[],
  fields: Field[],
  stored?: Filter[],
): Filter[] {
  const storedIds = new Set((stored ?? []).map(filterKey));
  // An operand-less operator (∅ / ⊙) is complete whatever its value cell held
  // before the switch (audit r4): flattened as is, a leftover `[]` became NO
  // row (and the editor called it unfinished), leftover picks became one
  // `∅ <pick>` row each. It goes out as ONE row with an empty value — the
  // backend reads no operand for it (GuestFilterKeyNormalizer.OPERAND_LESS).
  const operandsCleared = rows.map((f) =>
    isNoValueOperator(f.operator) ? { ...f, value: "" } : f,
  );
  return flattenArrayFilterValues(operandsCleared)
    .map((f) => {
      const key = canonicalFieldKey(fields, f.key);
      const dataType = findFieldByKey(fields, f.key)?.dataType;
      if (!dataType) return { ...f, key };
      const { operator, value } = coerceEmptyEqualsOperator(
        f.operator,
        f.value,
        dataType,
      );
      return { ...f, key, operator, value };
    })
    .filter((f) => {
      if (!f.key || !f.operator) return false;
      if (isNoValueOperator(f.operator)) return true;
      const hasOperand = Array.isArray(f.value)
        ? f.value.length > 0
        : f.value !== "";
      if (hasOperand) return true;
      // Empty operand: keep it only if this exact row is already persisted.
      // `filterKey` is compared against BOTH shapes because the stored row may
      // still carry the legacy bare key while `f.key` is canonical by now.
      return (
        storedIds.has(filterKey(f)) ||
        storedIds.has(filterKey({ ...f, key: stripAdditionalFieldsPrefix(f.key) }))
      );
    });
}

/** `additionalFields.tier` → `tier`; anything else unchanged. */
function stripAdditionalFieldsPrefix(key: string): string {
  return key.startsWith("additionalFields.")
    ? key.slice("additionalFields.".length)
    : key;
}

/** Stable identity key for a row filter — used to dedupe on import. */
export function filterKey(f: Filter): string {
  const v = Array.isArray(f.value) ? f.value.join(",") : f.value;
  return `${f.key}|${f.operator}|${v}`;
}

/**
 * Drop empty rows (no key/operator) and exact duplicates, preserving order.
 *
 * <p>`fields` canonicalizes the incoming keys first. It is OPTIONAL on purpose:
 * `PreviewFilterModal` feeds the merge result straight into
 * `parseToAdvancedFilter`, which resolves keys by BARE name and silently DROPS
 * what it cannot resolve — canonicalizing unconditionally inside the shared
 * helper would make imported custom-field rows vanish from the preview. Callers
 * that persist pass `fields`; callers that hand the rows to the bare-name parser
 * do not.
 */
function cleanDedupeFilters(incoming: Filter[], fields?: Field[]): Filter[] {
  const seen = new Set<string>();
  const out: Filter[] = [];
  for (const raw of incoming) {
    const f = fields ? { ...raw, key: canonicalFieldKey(fields, raw.key) } : raw;
    if (!f.key || !f.operator) continue;
    const k = filterKey(f);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(f);
  }
  return out;
}

/**
 * APPEND a saved guest-list filter's conditions to the template's existing
 * row filters, skipping exact duplicates so re-importing is idempotent.
 *
 * <p>A saved guest-list filter stores the BARE custom-field name (the flat
 * filter model does), while a row filter must carry `additionalFields.<name>`.
 * The old comment here claimed the backend prefixes at query time — it does that
 * for the guest LIST's own request, never for a role's row filter. Pass `fields`
 * to canonicalize; the dedupe then has to run on canonical keys on BOTH sides,
 * otherwise a pre-existing legacy `tier` row and an imported
 * `additionalFields.tier` read as two different filters.
 */
export function mergeRowFilters(
  existing: Filter[],
  incoming: Filter[],
  fields?: Field[],
): Filter[] {
  const base = fields ? cleanDedupeFilters(existing, fields) : existing;
  const seen = new Set(base.map(filterKey));
  const additions = cleanDedupeFilters(incoming, fields).filter(
    (f) => !seen.has(filterKey(f)),
  );
  return [...base, ...additions];
}

/** REPLACE the row filters with a saved filter's conditions (cleaned + deduped). */
export function replaceRowFilters(incoming: Filter[], fields?: Field[]): Filter[] {
  return cleanDedupeFilters(incoming, fields);
}

/**
 * Sort a saved view's columns into what the allowlist can carry.
 *
 *  - `kept` — allowlist keys (built-ins bare, custom as `additionalFields.<n>`),
 *    deduped, view order;
 *  - `implicit` — columns the allowlist never governs (row ids, action
 *    columns): the list shows them anyway, so they are neither stored nor
 *    reported as dropped;
 *  - `droppedLabels` — names nothing can model any more (typically a custom
 *    field deleted since the view was saved), for the footer's honesty line.
 */
export function categorizeViewFields(
  viewFields: string[],
  allFields: Field[],
  labelFor: (name: string) => string = (n) => n,
): { kept: string[]; implicit: string[]; droppedLabels: string[] } {
  const kept: string[] = [];
  const implicit: string[] = [];
  const droppedLabels: string[] = [];
  const keptSeen = new Set<string>();
  const implicitSeen = new Set<string>();
  const droppedSeen = new Set<string>();
  for (const n of viewFields) {
    const def = allFields.find((f) => f.name === n);
    const key = def?.additionalField ? `additionalFields.${n}` : n;
    if (isAllowlistExempt(key)) {
      if (!implicitSeen.has(key)) {
        implicitSeen.add(key);
        implicit.push(key);
      }
    } else if (isAllowlistableKey(key)) {
      if (!keptSeen.has(key)) {
        keptSeen.add(key);
        kept.push(key);
      }
    } else if (!droppedSeen.has(n)) {
      droppedSeen.add(n);
      droppedLabels.push(labelFor(n));
    }
  }
  return { kept, implicit, droppedLabels };
}

/**
 * REPLACE the field allowlist with a saved view's columns (mapped to allowlist
 * shape). Being a non-null array, this clears the "no restriction" state.
 */
export function viewFieldsToAllowlist(
  viewFields: string[],
  allFields: Field[],
): string[] {
  return categorizeViewFields(viewFields, allFields).kept;
}

/** ADD a saved view's columns onto the current allowlist (union, deduped). */
export function addViewToAllowlist(
  current: string[] | null,
  viewFields: string[],
  allFields: Field[],
): string[] {
  const kept = categorizeViewFields(viewFields, allFields).kept;
  return [...new Set([...(current ?? []), ...kept])];
}

/** Footer summary for importing a filter: how many land, how many are new vs already present. */
export function countFilterImport(
  current: Filter[],
  incoming: Filter[],
  fields?: Field[],
): { total: number; fresh: number; existing: number } {
  // Same key space as mergeRowFilters, or the footer promises rows the merge
  // then dedupes away.
  const uniq = cleanDedupeFilters(incoming, fields);
  const existingKeys = new Set(
    (fields ? cleanDedupeFilters(current, fields) : current).map(filterKey),
  );
  let fresh = 0;
  let existing = 0;
  for (const f of uniq) {
    if (existingKeys.has(filterKey(f))) existing++;
    else fresh++;
  }
  return { total: uniq.length, fresh, existing };
}

/** Footer summary for importing a view: kept count, how many are new, and dropped column labels. */
export function countViewImport(
  current: string[] | null,
  viewFields: string[],
  allFields: Field[],
): { kept: number; fresh: number; droppedLabels: string[] } {
  const { kept, droppedLabels } = categorizeViewFields(viewFields, allFields);
  const base = new Set(current ?? []);
  let fresh = 0;
  for (const k of kept) if (!base.has(k)) fresh++;
  return { kept: kept.length, fresh, droppedLabels };
}
